/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: AppInfoTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the branding module.
----------------------------------------------------------------*/

import { Settings } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import SimpleTooltip from '@/components/ui/SimpleTooltip';
import { FormBox } from '@/components/ui/FormBox';
import { useI18n } from '@/hooks/useI18n';
import { TabProps } from './types';

export const AppInfoTab: React.FC<TabProps> = ({
  formData,
  errors,
  onInputChange
}) => {
  const { t: safeT } = useI18n();

  return (
    <FormBox title={safeT('branding.appInformation')} icon={<Settings />}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
          <div className="space-y-1">
            <SimpleTooltip 
              content={safeT('branding.tooltips.appName', 'The name of your application as it appears in the interface.')}
              position="top"
              maxWidth="300px"
            >
              <label className="text-sm font-medium text-foreground">
                {safeT('branding.appName', 'Application Name')}
              </label>
            </SimpleTooltip>
          </div>
          <Input
            type="text"
            value={formData.appName || ''}
            onChange={(e) => onInputChange('appName', e.target.value)}
            placeholder={safeT('branding.placeholders.appName', 'Enter application name')}
            className={errors.appName ? 'border-destructive' : ''}
          />
          <div className="space-y-1">
            <SimpleTooltip 
              content={safeT('branding.tooltips.welcomeMessage', 'Welcome message shown to users when they log in.')}
              position="top"
              maxWidth="300px"
            >
              <label className="text-sm font-medium text-foreground">
                {safeT('branding.welcomeMessage', 'Welcome Message')}
              </label>
            </SimpleTooltip>
          </div>
          <Input
            type="text"
            value={formData.welcomeMessage || ''}
            onChange={(e) => onInputChange('welcomeMessage', e.target.value)}
            placeholder={safeT('branding.placeholders.welcomeMessage', 'Enter welcome message')}
            className={errors.welcomeMessage ? 'border-destructive' : ''}
          />

          <div className="space-y-1">
            <SimpleTooltip 
              content={safeT('branding.tooltips.primaryColor', 'Primary color used for buttons, links, and highlights.')}
              position="top"
              maxWidth="300px"
            >
              <label className="text-sm font-medium text-foreground">
                {safeT('branding.primaryColor', 'Primary Color')}
              </label>
            </SimpleTooltip>
          </div>
          <div className="flex gap-2">
            <input
              type="color"
              value={formData.primaryColor || '#3B82F6'}
              onChange={(e) => onInputChange('primaryColor', e.target.value)}
              className="w-12 h-10 rounded border border-input cursor-pointer"
            />
            <input
              type="text"
              value={formData.primaryColor || '#3B82F6'}
              onChange={(e) => onInputChange('primaryColor', e.target.value)}
              placeholder={safeT('branding.placeholders.primaryColor', '#3B82F6')}
              className="flex-1 px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="space-y-1">
            <SimpleTooltip 
              content={safeT('branding.tooltips.secondaryColor', 'Secondary color used for backgrounds and subtle elements.')}
              position="top"
              maxWidth="300px"
            >
              <label className="text-sm font-medium text-foreground">
                {safeT('branding.secondaryColor', 'Secondary Color')}
              </label>
            </SimpleTooltip>
          </div>
          <div className="flex gap-2">
            <input
              type="color"
              value={formData.secondaryColor || '#64748B'}
              onChange={(e) => onInputChange('secondaryColor', e.target.value)}
              className="w-12 h-10 rounded border border-input cursor-pointer"
            />
            <input
              type="text"
              value={formData.secondaryColor || '#64748B'}
              onChange={(e) => onInputChange('secondaryColor', e.target.value)}
              placeholder={safeT('branding.placeholders.secondaryColor', '#64748B')}
              className="flex-1 px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <SimpleTooltip 
              content={safeT('branding.tooltips.appDescription', 'Brief description of your application.')}
              position="top"
              maxWidth="300px"
            >
              <label className="text-sm font-medium text-foreground">
                {safeT('branding.appDescription', 'Description')}
              </label>
            </SimpleTooltip>
            <Textarea
              value={formData.appDescription || ''}
              onChange={(e) => onInputChange('appDescription', e.target.value)}
              placeholder={safeT('branding.placeholders.appDescription', 'Enter application description')}
              rows={3}
              className={errors.appDescription ? 'border-destructive' : ''}
            />
            {errors.appDescription && <p className="text-sm text-destructive">{errors.appDescription}</p>}
          </div>

          <div className="space-y-2">
            <SimpleTooltip 
              content={safeT('branding.tooltips.footerText', 'Footer text displayed at the bottom of the interface.')}
              position="top"
              maxWidth="300px"
            >
              <label className="text-sm font-medium text-foreground">
                {safeT('branding.footerText', 'Footer Text')}
              </label>
            </SimpleTooltip>
            <Input
              type="text"
              value={formData.footerText || ''}
              onChange={(e) => onInputChange('footerText', e.target.value)}
              placeholder={safeT('branding.placeholders.footerText', 'Enter footer text')}
              className={errors.footerText ? 'border-destructive' : ''}
            />
            {errors.footerText && <p className="text-sm text-destructive">{errors.footerText}</p>}
          </div>
        </div>
      </div>
    </FormBox>
  );
};
